import { Observable, BehaviorSubject, of as observableOf } from 'rxjs';
import { mergeMap, catchError, share } from "rxjs/operators";
import { addProvider, IProviderDescriptor } from "../ServiceUtil";
import { HelpDeskDelegate } from "./helpdesk.delegate";
import { AuthenticationService } from "../AuthenticationService";
import { IHelpDesk, IHelpDeskResponse, INewHelpDesk, INewHelpDeskComment } from "./helpdesk.interface";

export class HelpDeskService
{
    private helpdeskData: IHelpDesk[] = null;
    private helpdeskSubject: BehaviorSubject<IHelpDesk[]> = new BehaviorSubject(null);
    public helpdesks: Observable<IHelpDesk[]> = this.helpdeskSubject;

    private helpdeskFilteredSubject: BehaviorSubject<IHelpDesk[]> = new BehaviorSubject(null);
    public helpdesksFiltered: Observable<IHelpDesk[]> = this.helpdeskFilteredSubject;


    constructor(private helpDeskDelegate: HelpDeskDelegate, private authenticationService: AuthenticationService)
    {
    }

    public getAllHelpdesk(hardReload: boolean = false): void
    {
        if (this.helpdeskData !== null && this.helpdeskData.length > 0 && hardReload === false)
        {
            return;
        }
        const orgId = this.authenticationService.GetOrgId();
        const response = this.helpDeskDelegate.getAllHelpDeskRequest(orgId);
        this.processHelpDesks(response);
    }

    /**
     * Employee self-service: load helpdesk requests for the current employee
     * (formerly HelpDeskService.getAllHelpdeskByEmployee in employeeServicelib).
     */
    public getAllHelpdeskByEmployee(hardReload: boolean = false): void
    {
        if (this.helpdeskData !== null && this.helpdeskData.length > 0 && hardReload === false)
        {
            return;
        }
        const employeeId = (this.authenticationService as any).GetEmployeeId
            ? (this.authenticationService as any).GetEmployeeId()
            : this.authenticationService.GetUserId();
        const response = this.helpDeskDelegate.getAllHelpDeskRequestByEmployee(employeeId);
        this.processHelpDesks(response);
    }


    public getHelpdeskByEmployeeId(employeeId: string): IHelpDesk
    {
        return this.helpdeskData.find(helpdesk => helpdesk.employeeId === employeeId);
    }

    public getHelpdeskById(helpDeskRequestId: string): IHelpDesk
    {
        return this.helpdeskData.find(helpdesk => helpdesk.helpDeskRequestId === helpDeskRequestId);
    }

    public createNewHelpDesk(helpdesk: INewHelpDesk): Observable<string>
    {
        const result = this.helpDeskDelegate.createNewHelpDeskRequest({
            ...helpdesk,
            ...this.authenticationService.getBaseRequest
        });
        return result.pipe(mergeMap((response: IHelpDeskResponse) =>
        {
            if (!response)
            {
                return observableOf(null);
            }
            this.helpdeskData = response.helpDeskRequests;
            this.helpdeskSubject.next(this.helpdeskData);
            this.helpdeskFilteredSubject.next(this.helpdeskData);
            return observableOf(response.id);

        }),
            catchError((error: any) => observableOf(null)),
            share());
    }

    /** Employee self-service: create a helpdesk request. */
    public createNewHelpDeskByEmployee(helpdesk: INewHelpDesk): Observable<string>
    {
        const result = this.helpDeskDelegate.createNewHelpDeskRequestByEmployee({
            ...helpdesk,
            employeeId: this.authenticationService.GetEmployeeId(),
            ...this.authenticationService.getBaseRequest
        });
        return result.pipe(mergeMap((response: IHelpDeskResponse) =>
        {
            if (!response)
            {
                return observableOf(null);
            }
            this.helpdeskData = response.helpDeskRequests;
            this.helpdeskSubject.next(this.helpdeskData);
            this.helpdeskFilteredSubject.next(this.helpdeskData);
            return observableOf(response.id);

        }),
            catchError((error: any) => observableOf(null)),
            share());
    }

    /** Employee self-service: add a comment to a helpdesk request. */
    public createCommentByEmployee(helpdesk: INewHelpDeskComment): Observable<string>
    {
        const result = this.helpDeskDelegate.createCommentByEmployee({
            ...helpdesk,
            ...this.authenticationService.getBaseRequest
        });
        return result.pipe(mergeMap((response: IHelpDeskResponse) =>
        {
            if (!response)
            {
                return observableOf(null);
            }
            this.helpdeskData = response.helpDeskRequests;
            this.helpdeskSubject.next(this.helpdeskData);
            this.helpdeskFilteredSubject.next(this.helpdeskData);
            return observableOf(helpdesk.helpDeskRequestId);

        }),
            catchError((error: any) => observableOf(null)),
            share());
    }


    public createComment(helpdesk: INewHelpDeskComment): Observable<string>
    {
        const result = this.helpDeskDelegate.createComment({
            ...helpdesk,
            ...this.authenticationService.getBaseRequest
        });
        return result.pipe(mergeMap((response: IHelpDeskResponse) =>
        {
            if (!response)
            {
                return observableOf(null);
            }
            this.helpdeskData = response.helpDeskRequests;
            this.helpdeskSubject.next(this.helpdeskData);
            this.helpdeskFilteredSubject.next(this.helpdeskData);
            return observableOf(helpdesk.helpDeskRequestId);

        }),
            catchError((error: any) => observableOf(null)),
            share());
    }

    public updateHelpDesk(helpDesk: IHelpDesk): Observable<boolean>
    {
        var response = this.helpDeskDelegate.updateHelpDeskRequest({
            ...helpDesk,
            ...this.authenticationService.getBaseRequest
        });
        return this.processHelpDesks(response);
    }

    private processHelpDesks(helpDeskRequests: Observable<IHelpDesk[]>): Observable<boolean>
    {
        helpDeskRequests.subscribe(onSuccess.bind(this), onFault.bind(this));

        return helpDeskRequests.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share());

        function onSuccess(helpDeskRequests: IHelpDesk[])
        {
            this.helpdeskData = helpDeskRequests;
            this.helpdeskSubject.next(this.helpdeskData);
            this.helpdeskFilteredSubject.next(this.helpdeskData);
        }

        function onFault(error: any)
        {
            // HelpDeskService.logger.error(`processHelpDesks Fault - Error (${JSON.stringify(error)})`);
        }
    }
}